import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { searchApi } from '../services/api';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from './ui/command';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import {
  CheckSquare,
  Folder,
  Clock,
  Loader2,
  FileText,
  Users,
  LayoutDashboard,
  Calendar,
  Repeat,
  Focus,
  BarChart3,
  Trash2,
  Settings,
} from 'lucide-react';

const EMPTY_RESULTS = { tasks: [], projects: [], documents: [], teams: [], habits: [] };

const RECENT_KEY = 'busybee_search_recent';
const RECENT_LIMIT = 5;

const loadRecent = () => {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.slice(0, RECENT_LIMIT) : [];
  } catch {
    return [];
  }
};

const pushRecent = (item) => {
  try {
    const cur = loadRecent();
    const deduped = cur.filter((r) => !(r.type === item.type && r.id === item.id));
    const next = [item, ...deduped].slice(0, RECENT_LIMIT);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    /* ignore quota / private mode errors */
  }
};

const NAV_PAGES = [
  { key: 'home', path: '/app', icon: LayoutDashboard, navKey: 'nav.home' },
  { key: 'planner', path: '/app/planner', icon: Calendar, navKey: 'nav.planner' },
  { key: 'tasks', path: '/app/tasks', icon: CheckSquare, navKey: 'nav.my_tasks' },
  { key: 'projects', path: '/app/projects', icon: Folder, navKey: 'nav.projects' },
  { key: 'documents', path: '/app/documents', icon: FileText, navKey: 'nav.documents' },
  { key: 'habits', path: '/app/habits', icon: Repeat, navKey: 'nav.habits' },
  { key: 'focus_time', path: '/app/focus-time', icon: Focus, navKey: 'nav.focus_time' },
  { key: 'analytics', path: '/app/analytics', icon: BarChart3, navKey: 'nav.analytics' },
  { key: 'trash', path: '/app/trash', icon: Trash2, navKey: 'nav.trash' },
  { key: 'settings', path: '/app/settings', icon: Settings, navKey: 'nav.settings' },
];

const ResultIcon = ({ item }) => {
  switch (item.type) {
    case 'task':
      return <CheckSquare className="text-amber-700 dark:text-amber-400 flex-shrink-0" />;
    case 'project':
      return (
        <div
          className="h-4 w-4 rounded-sm flex-shrink-0"
          style={{ backgroundColor: item.color || '#3B82F6' }}
        />
      );
    case 'document':
      return item.icon ? (
        <span className="h-4 w-4 flex items-center justify-center text-sm leading-none flex-shrink-0">
          {item.icon}
        </span>
      ) : (
        <FileText className="text-sky-400 flex-shrink-0" />
      );
    case 'team':
      return (
        <Users
          className="flex-shrink-0"
          style={{ color: item.color || '#8B5CF6' }}
        />
      );
    case 'habit':
      return (
        <Clock
          className="flex-shrink-0"
          style={{ color: item.color || '#F59E0B' }}
        />
      );
    default:
      return null;
  }
};

const GlobalSearch = ({ open, onOpenChange, onTaskSelect }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(EMPTY_RESULTS);
  const [loading, setLoading] = useState(false);
  const [recent, setRecent] = useState([]);
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setRecent(loadRecent());
    } else {
      setQuery('');
      setResults(EMPTY_RESULTS);
      setLoading(false);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    }
  }, [open]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleQueryChange = useCallback((value) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (value.length < 2) {
      setResults(EMPTY_RESULTS);
      setLoading(false);
      return;
    }

    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await searchApi.search(value);
        setResults({ ...EMPTY_RESULTS, ...res.data });
      } catch (err) {
        console.error('Search error:', err);
        setResults(EMPTY_RESULTS);
      } finally {
        setLoading(false);
      }
    }, 250);
  }, []);

  const navigateForItem = useCallback(
    (item) => {
      switch (item.type) {
        case 'task':
          if (onTaskSelect) onTaskSelect(item);
          break;
        case 'project':
          navigate(`/app/projects?project=${item.id}`);
          break;
        case 'document':
          navigate(`/app/documents/${item.id}`);
          break;
        case 'team':
          navigate(`/app/tasks?team=${item.id}`);
          break;
        case 'habit':
          navigate('/app/habits');
          break;
        default:
          break;
      }
    },
    [navigate, onTaskSelect]
  );

  const handleResultClick = useCallback(
    (item) => {
      const stored = {
        type: item.type,
        id: item.id,
        title: item.title,
        subtitle: item.subtitle || null,
        color: item.color || null,
        icon: item.icon || null,
        project_id: item.project_id || null,
        team_id: item.team_id || null,
      };
      pushRecent(stored);
      onOpenChange(false);
      navigateForItem(item);
    },
    [onOpenChange, navigateForItem]
  );

  const handlePageClick = useCallback(
    (page) => {
      onOpenChange(false);
      navigate(page.path);
    },
    [navigate, onOpenChange]
  );

  const filteredPages = useMemo(() => {
    if (!query) return NAV_PAGES;
    const q = query.toLowerCase();
    return NAV_PAGES.filter((p) => {
      const label = t(p.navKey).toLowerCase();
      return label.includes(q) || p.key.includes(q);
    });
  }, [query, t]);

  const hasServerResults =
    results.tasks.length > 0 ||
    results.projects.length > 0 ||
    results.documents.length > 0 ||
    results.teams.length > 0 ||
    results.habits.length > 0;

  const showRecent = query.length === 0 && recent.length > 0;
  const showPages = filteredPages.length > 0;
  const noResultsForQuery =
    !loading && query.length >= 2 && !hasServerResults && filteredPages.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden p-0 bg-popover border border-border rounded-xl shadow-2xl max-w-2xl [&>button]:hidden max-md:rounded-none max-md:border-0 max-md:max-w-none max-md:w-screen max-md:h-[100dvh] max-md:top-0 max-md:left-0 max-md:translate-x-0 max-md:translate-y-0 max-md:pt-[env(safe-area-inset-top)] max-md:pb-[env(safe-area-inset-bottom)]">
        <DialogTitle className="sr-only">{t('search.placeholder')}</DialogTitle>
        <Command
          shouldFilter={false}
          className={[
            'bg-transparent text-foreground',
            '[&_[cmdk-input-wrapper]]:border-b [&_[cmdk-input-wrapper]]:border-border [&_[cmdk-input-wrapper]]:px-4',
            '[&_[cmdk-input-wrapper]:focus-within]:border-amber-500/60',
            '[&_[cmdk-input-wrapper]_svg]:text-muted-foreground [&_[cmdk-input-wrapper]_svg]:h-4 [&_[cmdk-input-wrapper]_svg]:w-4',
            '[&_[cmdk-input]]:h-12 [&_[cmdk-input]]:bg-transparent [&_[cmdk-input]]:text-foreground [&_[cmdk-input]]:placeholder:text-muted-foreground [&_[cmdk-input]]:text-sm [&_[cmdk-input]]:caret-amber-500 [&_[cmdk-input]]:outline-none',
            '[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider',
            '[&_[cmdk-group]]:px-2 [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0',
            '[&_[cmdk-item]]:px-3 [&_[cmdk-item]]:py-2.5 [&_[cmdk-item]]:rounded-lg [&_[cmdk-item]]:text-foreground [&_[cmdk-item]]:cursor-pointer',
            '[&_[cmdk-item]:hover]:bg-muted [&_[cmdk-item]:hover]:text-foreground',
            '[&_[cmdk-item][data-selected=true]]:bg-muted [&_[cmdk-item][data-selected=true]]:text-foreground',
            '[&_[cmdk-item]_svg]:h-4 [&_[cmdk-item]_svg]:w-4 [&_[cmdk-item]_svg]:shrink-0',
          ].join(' ')}
        >
          <div className="relative">
            <CommandInput
              placeholder={t('search.placeholder')}
              value={query}
              onValueChange={handleQueryChange}
              className="max-md:pr-[78px]"
            />
            {/* Mobile: explicit Cancel button (iOS Spotlight pattern). The
                Radix default close X is hidden via [&>button]:hidden so we
                provide a clear, large hit target here instead. */}
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="md:hidden absolute right-3 top-1/2 -translate-y-1/2 px-2 py-1 text-amber-500 font-medium text-[15px] active:opacity-60"
            >
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </button>
          </div>
          <CommandList className="max-h-[440px] md:max-h-[440px] max-md:max-h-[calc(100dvh-7rem)] py-2">
            {loading && (
              <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('search.searching')}
              </div>
            )}

            {!loading && noResultsForQuery && (
              <CommandEmpty className="py-8 text-center text-sm text-muted-foreground">
                {t('search.no_results_for', { query })}
              </CommandEmpty>
            )}

            {!loading && query.length === 0 && recent.length === 0 && (
              <div className="py-8 text-center text-sm text-muted-foreground px-4">
                {t('search.type_to_search')}
              </div>
            )}

            {!loading && showRecent && (
              <>
                <CommandGroup heading={t('search.recent')}>
                  {recent.map((item) => (
                    <CommandItem
                      key={`recent-${item.type}-${item.id}`}
                      value={`recent-${item.type}-${item.id}`}
                      onSelect={() => handleResultClick(item)}
                    >
                      <ResultIcon item={item} />
                      <div className="flex min-w-0 flex-col">
                        <span className="truncate">{item.title || t('documents.untitled')}</span>
                        {item.subtitle && (
                          <span className="truncate text-xs text-muted-foreground">
                            {item.subtitle}
                          </span>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
                {showPages && <CommandSeparator className="bg-border my-1" />}
              </>
            )}

            {/* Server results */}
            {!loading && results.tasks.length > 0 && (
              <>
                <CommandGroup heading={t('search.tasks')}>
                  {results.tasks.map((task) => (
                    <CommandItem
                      key={`task-${task.id}`}
                      value={`task-${task.id}`}
                      onSelect={() => handleResultClick(task)}
                    >
                      <ResultIcon item={task} />
                      <div className="flex min-w-0 flex-col">
                        <span className="truncate">{task.title}</span>
                        {task.subtitle && (
                          <span className="truncate text-xs text-muted-foreground">
                            {task.subtitle}
                          </span>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
                <CommandSeparator className="bg-border my-1" />
              </>
            )}

            {!loading && results.projects.length > 0 && (
              <>
                <CommandGroup heading={t('search.projects')}>
                  {results.projects.map((project) => (
                    <CommandItem
                      key={`project-${project.id}`}
                      value={`project-${project.id}`}
                      onSelect={() => handleResultClick(project)}
                    >
                      <ResultIcon item={project} />
                      <div className="flex min-w-0 flex-col">
                        <span className="truncate">{project.title}</span>
                        {project.subtitle && (
                          <span className="truncate text-xs text-muted-foreground">
                            {project.subtitle}
                          </span>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
                <CommandSeparator className="bg-border my-1" />
              </>
            )}

            {!loading && results.documents.length > 0 && (
              <>
                <CommandGroup heading={t('search.documents')}>
                  {results.documents.map((doc) => (
                    <CommandItem
                      key={`document-${doc.id}`}
                      value={`document-${doc.id}`}
                      onSelect={() => handleResultClick(doc)}
                    >
                      <ResultIcon item={doc} />
                      <span className="truncate">{doc.title || t('documents.untitled')}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
                <CommandSeparator className="bg-border my-1" />
              </>
            )}

            {!loading && results.teams.length > 0 && (
              <>
                <CommandGroup heading={t('search.teams')}>
                  {results.teams.map((team) => (
                    <CommandItem
                      key={`team-${team.id}`}
                      value={`team-${team.id}`}
                      onSelect={() => handleResultClick(team)}
                    >
                      <ResultIcon item={team} />
                      <div className="flex min-w-0 flex-col">
                        <span className="truncate">{team.title}</span>
                        {team.subtitle && (
                          <span className="truncate text-xs text-muted-foreground">
                            {team.subtitle}
                          </span>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
                <CommandSeparator className="bg-border my-1" />
              </>
            )}

            {!loading && results.habits.length > 0 && (
              <>
                <CommandGroup heading={t('search.habits')}>
                  {results.habits.map((habit) => (
                    <CommandItem
                      key={`habit-${habit.id}`}
                      value={`habit-${habit.id}`}
                      onSelect={() => handleResultClick(habit)}
                    >
                      <ResultIcon item={habit} />
                      <span className="truncate">{habit.title}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
                <CommandSeparator className="bg-border my-1" />
              </>
            )}

            {!loading && showPages && (
              <CommandGroup heading={t('search.pages')}>
                {filteredPages.map((page) => {
                  const Icon = page.icon;
                  return (
                    <CommandItem
                      key={`page-${page.key}`}
                      value={`page-${page.key}`}
                      onSelect={() => handlePageClick(page)}
                    >
                      <Icon className="text-muted-foreground" />
                      <span className="truncate">{t(page.navKey)}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>

          <div className="hidden md:flex border-t border-border px-4 py-2 text-[11px] text-muted-foreground/80 items-center justify-end gap-2">
            <span>{t('search.shortcut_hint')}</span>
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
};

export default GlobalSearch;
